import { useEffect, useMemo, useRef } from 'react';
import useWorkflowChat from './hooks/useWorkflowChat';
import ChatMessage from './components/ChatMessage';
import ChatInput from './components/ChatInput';
import TypingIndicator from './components/TypingIndicator';
import WorkflowPreviewPanel from './components/WorkflowPreviewPanel';
import { WorkflowPreviewBlock } from './types';
import { User } from 'common/components/UserAvatar/UserAvatar';
import BEM from 'common/bem';
import mobxInjectSelect from 'common/utils/mobxInjectSelect';

const classes = BEM.with('ClinicalWorkflows');

const SCROLL_NEAR_BOTTOM_PX = 80;

const EVENT_TYPES = [
  {
    value: 'Nurse Call',
    label: 'Nurse Call Alarms',
    description: 'Nurse call button alarm workflows',
  },
  {
    value: 'Patient Monitor',
    label: 'Patient Monitor Alarms',
    description: 'Bedside monitor alarm workflows',
  },
  {
    value: 'Clinical Orders',
    label: 'Clinical Orders (ORM)',
    description: 'EHR consult, admission, and order events',
  },
  {
    value: 'ADT',
    label: 'Admit / Discharge / Transfer',
    description: 'Patient admit, discharge, and transfer events',
  },
  {
    value: 'Lab Results',
    label: 'Lab & Diagnostic Results',
    description: 'HL7 lab and diagnostic result events',
  },
  {
    value: 'EMS Case',
    label: 'EMS Case Alerts',
    description: 'EMS case alerts from Twiage',
  },
];

type ClinicalWorkflowsProps = {};

type MobxProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  client: Record<string, any> | null;
  currentOrganization: { displayName: string; name: string } | null;
  currentOrganizationId: string;
  currentUser: User | null;
};

type CombinedProps = ClinicalWorkflowsProps & MobxProps;

function ClinicalWorkflows({
  client,
  currentOrganization,
  currentOrganizationId,
  currentUser,
}: CombinedProps) {
  const messageListRef = useRef<HTMLDivElement>(null);

  const orgContext = useMemo(
    () =>
      currentOrganization
        ? { orgName: currentOrganization.displayName, orgToken: currentOrganizationId }
        : null,
    [currentOrganization, currentOrganizationId]
  );

  const { messages, isLoading, activeTool, error, sendMessage, selectOption } = useWorkflowChat(
    client,
    orgContext
  );

  // When a tool is active, find the label from the nearest preceding started block.
  const activeProgressMessage = useMemo(() => {
    if (!activeTool) return null;
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.role !== 'assistant') continue;
      for (let j = msg.segments.length - 1; j >= 0; j--) {
        const seg = msg.segments[j];
        if (seg.kind === 'progress' && seg.data.status === 'started') {
          return seg.data.message ?? null;
        }
      }
    }
    return null;
  }, [activeTool, messages]);

  const latestPreview = useMemo<WorkflowPreviewBlock | null>(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.role !== 'assistant') continue;
      for (let j = msg.segments.length - 1; j >= 0; j--) {
        const seg = msg.segments[j];
        if (seg.kind === 'workflow_preview') {
          return seg.data;
        }
      }
    }
    return null;
  }, [messages]);

  useEffect(() => {
    const el = messageListRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distFromBottom < SCROLL_NEAR_BOTTOM_PX) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  const lastAssistantIdx = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.role !== 'assistant') continue;
      // Skip progress-only messages — they have no displayable content and would
      // incorrectly hide question prompts on the preceding substantive message.
      const isProgressOnly =
        msg.segments.length > 0 && msg.segments.every((s) => s.kind === 'progress');
      if (!isProgressOnly) return i;
    }
    return -1;
  })();

  const hasUserRepliedToLast =
    lastAssistantIdx >= 0 && messages.slice(lastAssistantIdx + 1).some((m) => m.role === 'user');

  const showWelcome = messages.length === 0 && !isLoading;

  const lastMsg = messages[messages.length - 1];
  // Disable input while: no client, a tool is running, the last assistant message is still
  // streaming, or we're waiting for the first AI token after the user sent a message.
  // Intentionally NOT waiting for the `done` SSE event — the last message token arrives
  // before `done`, and that gap is what caused the noticeable input delay.
  const isInputDisabled =
    !client ||
    activeTool !== null ||
    !!(lastMsg?.role === 'assistant' && lastMsg?.isStreaming) ||
    (isLoading && lastMsg?.role !== 'assistant');

  const handleEventSelect = (eventType: string) => {
    sendMessage(`Configure ${eventType} workflow`);
  };

  return (
    <div className={classes()}>
      <div className={classes('header')}>
        <div className={classes('header-icon')}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
          </svg>
        </div>
        <div className={classes('header-text')}>
          <h2 className={classes('title')}>Build Workflow</h2>
          <p className={classes('subtitle')}>Describe your automation in natural language</p>
        </div>
      </div>

      <div className={classes('split')}>
        <div className={classes('chat-pane')}>
          <div className={classes('message-list')} ref={messageListRef}>
            {showWelcome && (
              <div className={classes('welcome')}>
                <div className={classes('welcome-icon')}>
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                  </svg>
                </div>
                <h3 className={classes('welcome-title')}>
                  What type of event would you like to configure?
                </h3>
                <p className={classes('welcome-subtitle')}>
                  Select an event type to get started with your workflow configuration.
                </p>
                <div className={classes('welcome-grid')}>
                  {EVENT_TYPES.map((evt) => (
                    <button
                      key={evt.value}
                      className={classes('welcome-btn')}
                      onClick={() => handleEventSelect(evt.value)}
                      disabled={!client}
                      type="button"
                    >
                      <span className={classes('welcome-btn-label')}>{evt.label}</span>
                      <span className={classes('welcome-btn-desc')}>{evt.description}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg, idx) => (
              <ChatMessage
                key={msg.id}
                message={msg}
                currentUser={currentUser ?? undefined}
                onSelectOption={selectOption}
                isLastAssistantMessage={idx === lastAssistantIdx}
                hasUserReplied={idx === lastAssistantIdx && hasUserRepliedToLast}
              />
            ))}
            {(isLoading || activeTool !== null) &&
            messages[messages.length - 1]?.role !== 'assistant' ? (
              <TypingIndicator message={activeProgressMessage ?? undefined} />
            ) : null}
          </div>

          {error && !isLoading && (
            <div className={classes('error-bar')}>
              <span>{error}</span>
            </div>
          )}

          <ChatInput onSend={sendMessage} disabled={isInputDisabled} />
        </div>

        <div className={classes('preview-pane')}>
          <WorkflowPreviewPanel preview={latestPreview} />
        </div>
      </div>
    </div>
  );
}

export default mobxInjectSelect<ClinicalWorkflowsProps, MobxProps>({
  messengerStore: ['client', 'currentOrganization', 'currentOrganizationId'],
  sessionStore: ['currentUser'],
})(ClinicalWorkflows);
